<template>
  <base-modal
    :ref="modal.key"
    :show="open"
    :title="modalTitle"
    :subtitle="digitalSignature.name"
    @on-confirm="saveItem"
    @on-cancel="hide()"
    :okText="$t('Save')"
    :closeText="$t('Close')"
    maxWidth="60%"
  >
    <v-tabs centered v-model="selectedTab">
      <v-tab>{{ $t("Description") }}</v-tab>
      <v-tab>{{ $t("Signature") }}</v-tab>
      <v-tab-item>
        <v-card flat class="my-0">
          <v-card-text>
            <v-text-field
              v-bind:label="$t('Name')"
              v-model="digitalSignature.name"
              :error-messages="nameErrors"
              @input="$v.digitalSignature.name.$touch()"
              @blur="$v.digitalSignature.name.$touch()"
            ></v-text-field>

            <v-select
              v-model="digitalSignature.digitalSignatureProviderId"
              v-bind:label="$t('Provider')"
              :items="providerList"
              item-text="text"
              item-value="id"
              :error-messages="digitalSignatureProviderIdErrors"
              @change="$v.digitalSignature.digitalSignatureProviderId.$touch()"
              @blur="$v.digitalSignature.digitalSignatureProviderId.$touch()"
            ></v-select>

            <div
              v-if="
                digitalSignature.digitalSignatureProviderId ==
                providers.QuoVadis
              "
            >
              <v-text-field
                v-bind:label="$t('Profile')"
                v-model="digitalSignature.profile"
                :error-messages="profileErrors"
                @input="$v.digitalSignature.profile.$touch()"
                @blur="$v.digitalSignature.profile.$touch()"
              ></v-text-field>

              <v-text-field
                v-bind:label="$t('ClientId')"
                v-model="digitalSignature.clientId"
                :error-messages="clientIdErrors"
                @input="$v.digitalSignature.clientId.$touch()"
                @blur="$v.digitalSignature.clientId.$touch()"
              ></v-text-field>
              <v-text-field
                v-bind:label="$t('PIN')"
                v-model="digitalSignature.pinNumber"
                :error-messages="pinNumberErrors"
                @input="$v.digitalSignature.pinNumber.$touch()"
                @blur="$v.digitalSignature.pinNumber.$touch()"
              ></v-text-field>

              <v-text-field
                v-bind:label="$t('Login')"
                v-model="digitalSignature.login"
                :error-messages="loginErrors"
                @input="$v.digitalSignature.login.$touch()"
                @blur="$v.digitalSignature.login.$touch()"
              ></v-text-field>

              <v-text-field
                v-bind:label="$t('Password')"
                type="password"
                v-model="digitalSignature.password"
                :error-messages="passwordErrors"
                @input="$v.digitalSignature.password.$touch()"
                @blur="$v.digitalSignature.password.$touch()"
              ></v-text-field>
            </div>
          </v-card-text>
        </v-card>
      </v-tab-item>
      <v-tab-item>
        <v-card flat class="my-0">
          <v-card-text>
            <v-checkbox
              v-model="digitalSignature.signatureVisible"
              v-bind:label="$t('SignatureVisible')"
            ></v-checkbox>
            <div v-if="digitalSignature.signatureVisible">
              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-bind:label="`${$t('Position')} (X) (mm)`"
                    v-model="digitalSignature.signatureLocationX"
                    min="1"
                    step="1"
                    type="number"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-bind:label="`${$t('Position')} (Y) (mm)`"
                    v-model="digitalSignature.signatureLocationY"
                    min="1"
                    step="1"
                    type="number"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-bind:label="`${$t('Size')} (X) (mm)`"
                    v-model="digitalSignature.signatureSizeX"
                    min="1"
                    step="1"
                    type="number"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-bind:label="`${$t('Size')} (Y) (mm)`"
                    v-model="digitalSignature.signatureSizeY"
                    min="1"
                    step="1"
                    type="number"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-bind:label="$t('Page')"
                    v-model="digitalSignature.signatureLocationPage"
                    :disabled ="digitalSignature.lastPageSignature"
                    min="1"
                    step="1"
                    type="number"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="3">
                  <v-checkbox
                    v-model="digitalSignature.lastPageSignature"
                    v-bind:label="$t('LastPage')"
                  ></v-checkbox>
                </v-col>
              </v-row>
              <v-row
                dense
                v-if="
                  digitalSignature.digitalSignatureProviderId ==
                  providers.QuoVadis
                "
              >
                <v-col cols="12" md="6">
                  <v-checkbox
                    v-model="digitalSignature.signatureIncludeDescription"
                    v-bind:label="$t('IncludeDescription')"
                  ></v-checkbox>
                </v-col>
              </v-row>
              <v-row
                dense
                v-if="
                  digitalSignature.digitalSignatureProviderId ==
                  providers.ElectronicSignature
                "
              >
                <v-col cols="12" md="4">
                  <v-checkbox
                    v-model="digitalSignature.signatureIncludeFrame"
                    v-bind:label="$t('IncludeFrame')"
                  ></v-checkbox>
                </v-col>
                <v-col cols="12" md="4">
                  <v-checkbox
                    v-model="digitalSignature.signatureIncludeUserFullName"
                    v-bind:label="$t('IncludeUserFullName')"
                  ></v-checkbox>
                </v-col>
                <v-col cols="12" md="4">
                  <v-checkbox
                    v-model="digitalSignature.signatureIncludeTimeStamp"
                    v-bind:label="$t('IncludeTimeStamp')"
                  ></v-checkbox>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="12" md="4">
                  <v-checkbox
                    v-model="digitalSignature.signatureIncludeImage"
                    v-bind:label="$t('IncludeImage')"
                  ></v-checkbox>
                </v-col>
                <v-col
                  cols="12"
                  md="4"
                  v-if="digitalSignature.signatureIncludeImage"
                >
                  <v-checkbox
                    v-model="digitalSignature.useUserSignatureImage"
                    v-bind:label="$t('UseUserDefaultSignature')"
                  ></v-checkbox>
                </v-col>
                <v-col cols="12" md="4">
                  <v-checkbox
                    v-model="digitalSignature.dateShifted"
                    v-bind:label="$t('DateShifted')"
                  ></v-checkbox>
                </v-col>
              </v-row>
              <v-row
                dense
                v-show="
                  digitalSignature.signatureIncludeImage &&
                  !digitalSignature.useUserSignatureImage
                "
              >
                <v-col>
                  <ImageCropper :oldImage="digitalSignature.signatureImage"
                                :onChange="changeSignatureImage"
                                ref="imageCropper">

                  </ImageCropper>
                </v-col>
              </v-row>
            </div>
            <v-row
              dense
              v-if="
                digitalSignature.digitalSignatureProviderId ==
                providers.ElectronicSignature
              "
            >
              <v-col cols="12" md="6">
                <v-checkbox
                  v-model="digitalSignature.signatureIncludeCertificate"
                  v-bind:label="$t('IncludeCertificate')"
                ></v-checkbox>
              </v-col>
              <v-col
                cols="12"
                md="6"
                v-if="digitalSignature.signatureIncludeCertificate"
              >
                <v-checkbox
                  v-model="digitalSignature.useUserCertificate"
                  v-bind:label="$t('UseUserCertificate')"
                ></v-checkbox>
              </v-col>
            </v-row>
            <v-row
              dense
              v-if="
                digitalSignature.signatureIncludeCertificate &&
                !digitalSignature.useUserCertificate
              "
            >
              <v-col>
                <v-file-input
                  v-if="allowEditCertificate"
                  v-bind:label="$t('Certificate')"
                  v-model="signatureCertificate"
                  accept=".pfx"
                ></v-file-input>
                <v-text-field
                  readonly
                  v-if="!allowEditCertificate"
                  v-model="digitalSignature.signatureCertificateFileName"
                  :label="$t('Certificate')"
                  append-icon="mdi-pencil"
                  @click:append="editCertificate()"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs>
  </base-modal>
</template>
<script>
import { required, requiredIf } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";
import DigitalSignatureService from "@/services/DigitalSignatureService";
import UserService from "@/services/UserService";
import DigitalSignature from "@/ecm/models/DigitalSignature";
import DigitalSignatureProviders from "@/ecm/variables/DigitalSignatureProviders";
import Utils from "@/ecm/common/Utils";
import Signature from "../../../ecm/models/Signature";
import ImageCropper from "@/components/base/ImageCropper.vue";

export default {
  name: "DigitalSignatureModal",
  components: {ImageCropper },
  created() {
    DigitalSignatureService.getProviderList().then((data) => {
      this.providerList = data;
    });

    this.$v.$reset();
  },
  data() {
    return {
      providerList: [],
      providers: DigitalSignatureProviders,
      modal: {
        title: this.$t("DigitalSignature"),
        key: "digitalSignatureModal",
      },
      open: false,
      digitalSignature: new DigitalSignature(),
      allowEditCertificate: true, //Ugly fix for signature img in edition
      signatureCertificate: {},
      selectedSignatureId: null,
      selectedTab: 0,
      isNew: true,
    };
  },
  mixins: [validationMixin],
  validations: {
    digitalSignature: {
      name: { required },
      profile: {
        required: requiredIf(function () {
          return (
            this.digitalSignature.digitalSignatureProviderId ==
            this.providers.QuoVadis
          );
        }),
      },
      clientId: {
        required: requiredIf(function () {
          return (
            this.digitalSignature.digitalSignatureProviderId ==
            this.providers.QuoVadis
          );
        }),
      },
      pinNumber: {
        required: requiredIf(function () {
          return (
            this.digitalSignature.digitalSignatureProviderId ==
            this.providers.QuoVadis
          );
        }),
      },
      login: {
        required: requiredIf(function () {
          return (
            this.digitalSignature.digitalSignatureProviderId ==
            this.providers.QuoVadis
          );
        }),
      },
      password: {
        required: requiredIf(function () {
          return (
            this.digitalSignature.digitalSignatureProviderId ==
            this.providers.QuoVadis
          );
        }),
      },
      digitalSignatureProviderId: { required },
    },
  },
  computed: {
    modalInstance() {
      return this.$refs[this.modal.key];
    },
    modalTitle() {
      return (
        this.modal.title +
        (this.digitalSignature.name ? " - " + this.digitalSignature.name : "")
      );
    },
    digitalSignatureProviderIdErrors() {
      const errors = [];
      if (!this.$v.digitalSignature.digitalSignatureProviderId.$dirty)
        return errors;
      !this.$v.digitalSignature.digitalSignatureProviderId.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    passwordErrors() {
      const errors = [];
      if (!this.$v.digitalSignature.password.$dirty) return errors;
      !this.$v.digitalSignature.password.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    profileErrors() {
      const errors = [];
      if (!this.$v.digitalSignature.profile.$dirty) return errors;
      !this.$v.digitalSignature.profile.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    clientIdErrors() {
      const errors = [];
      if (!this.$v.digitalSignature.clientId.$dirty) return errors;
      !this.$v.digitalSignature.clientId.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    pinNumberErrors() {
      const errors = [];
      if (!this.$v.digitalSignature.pinNumber.$dirty) return errors;
      !this.$v.digitalSignature.pinNumber.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    loginErrors() {
      const errors = [];
      if (!this.$v.digitalSignature.login.$dirty) return errors;
      !this.$v.digitalSignature.login.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    nameErrors() {
      const errors = [];
      if (!this.$v.digitalSignature.name.$dirty) return errors;
      !this.$v.digitalSignature.name.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
  },
  watch: {
    signatureCertificate() {
      if (this.signatureCertificate) {
        Utils.file.toBase64(this.signatureCertificate).then((base64) => {
          this.digitalSignature.signatureCertificateBase64 =
            Utils.file.image.getBase64(base64).data;
          this.digitalSignature.signatureCertificateFileName =
            this.signatureCertificate.name;
        });
      }
    },
  },
  methods: {
    saveItem() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.selectedTab = 0;
        return;
      }

      this.modalInstance.progress(true);
      let that = this;
      DigitalSignatureService.save(this.digitalSignature.toDto())
        .then((result) => {
          if (result) {
            if (this.isNew) {
              Utils.toast.success(
                this.$t("DigitalSignature") +
                  " " +
                  this.$t("CreatedSuccessfullyFem")
              );
            } else {
              Utils.toast.success(
                this.$t("DigitalSignature") +
                  " " +
                  this.$t("UpdatedSuccessfullyFem")
              );
            }
            this.hide();
            this.$emit("on-item-saved");
          }
        })
        .catch((error) => {
          // error
        })
        .then(() => {
          //always
          this.modalInstance.progress(false);
        });
    },
    new() {
      this.selectedTab = 0;
      this.digitalSignature = new DigitalSignature();
      this.signatureCertificate = null;
      this.allowEditCertificate = true;
      this.$v.$reset();
      this.show();
    },
    editCertificate() {
      this.allowEditCertificate = true;
      this.digitalSignature.signatureCertificateFileName = "";
    },
    edit(dto) {
      this.selectedTab = 0;
      this.isNew = false;
      this.digitalSignature = new DigitalSignature(dto);
      this.allowEditCertificate = true;
      if (dto.SignatureCertificatePath) {
        this.allowEditCertificate = false;
      }
      if (dto.SignatureImagePath) {
        DigitalSignatureService.getSignatureImage(
          this.digitalSignature.id
        ).then((result) => {
          if (result) {
            this.digitalSignature.signatureImage =
              Utils.file.image.toBase64(result);
          }
        });
      }

      this.show();
    },
    show() {
      this.open = true;
    },
    hide() {
      this.open = false;
      this.isNew = true;

      if (this.$refs.imageCropper)
        this.$refs.imageCropper.reset();
    },
    changeSignatureImage(newImage) {
      this.digitalSignature.signatureImage = newImage;
    }
  },
};
</script>